/**
 * Push im Browser (F-31): Service Worker, Erlaubnis, Abo. Das Geraet merkt
 * sich lokal, dass es angemeldet ist - fehlt das Abo spaeter (Browserdaten
 * geloescht, Abo abgelaufen), erkennt die App das als "verloren".
 *
 * Empfangen und Plan liefern sind getrennt: Die Daten liegen je Browser.
 * Das Geraet mit den Daten (z. B. der Laptop) liefert den Plan
 * ("Planquelle"), das Handy empfaengt nur - sonst ueberschriebe sein leerer
 * Plan den richtigen.
 */
import { create } from 'zustand';

import { dienstStatus, geraetAbmelden, geraetAnmelden, type PlanStand } from '@/modules/benachrichtigungen/push/api';

const SPEICHER = 'ole-privat:push';
const PLANQUELLE = 'ole-privat:push-planquelle';
export const ZULETZT_GESENDET = 'ole-privat:push-plan';

function planQuelleLesen(): boolean {
  try { return localStorage.getItem(PLANQUELLE) === '1'; } catch { return false; }
}

interface Lokal { geraetId: string; endpoint: string }

export type PushZustand = 'pruefe' | 'nicht-unterstuetzt' | 'kein-dienst' | 'blockiert' | 'aus' | 'an' | 'verloren';

function lokalLesen(): Lokal | null {
  try { return JSON.parse(localStorage.getItem(SPEICHER) ?? 'null') as Lokal | null; } catch { return null; }
}
function lokalSchreiben(l: Lokal | null): void {
  try { if (l) localStorage.setItem(SPEICHER, JSON.stringify(l)); else localStorage.removeItem(SPEICHER); } catch { /* ohne Speicher kein Merken */ }
}

const unterstuetzt = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof window !== 'undefined' && 'PushManager' in window && typeof Notification !== 'undefined';

function schluessel(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

interface PushStore {
  /** Empfangen auf diesem Geraet. */
  zustand: PushZustand;
  geraetId: string | null;
  /** Ist der Dienst erreichbar? `null` = wird geprueft. */
  dienst: boolean | null;
  /** Liefert dieses Geraet den Plan? */
  planQuelle: boolean;
  planStand: PlanStand | null;
  pruefen: () => Promise<void>;
  setPlanQuelle: (an: boolean) => void;
}

export const usePush = create<PushStore>((set, get) => ({
  zustand: 'pruefe',
  geraetId: lokalLesen()?.geraetId ?? null,
  dienst: null,
  planQuelle: planQuelleLesen(),
  planStand: null,
  pruefen: async () => {
    const status = await dienstStatus();
    set({ ...(await ermitteln(status.verfuegbar)), dienst: status.verfuegbar, planStand: status.plan ?? null });
  },
  setPlanQuelle: (an) => {
    try {
      if (an) localStorage.setItem(PLANQUELLE, '1'); else localStorage.removeItem(PLANQUELLE);
      // Beim Einschalten sofort senden, auch wenn sich seit dem letzten Mal nichts geaendert hat.
      localStorage.removeItem(ZULETZT_GESENDET);
    } catch { /* ohne Speicher gilt es nur bis zum Neuladen */ }
    set({ planQuelle: an });
    void get().pruefen();
  },
}));

async function ermitteln(verfuegbar: boolean): Promise<{ zustand: PushZustand; geraetId: string | null }> {
  const lokal = lokalLesen();
  if (!unterstuetzt()) return { zustand: 'nicht-unterstuetzt', geraetId: null };
  if (!verfuegbar) return { zustand: 'kein-dienst', geraetId: lokal?.geraetId ?? null };
  if (Notification.permission === 'denied') return { zustand: 'blockiert', geraetId: lokal?.geraetId ?? null };
  if (!lokal) return { zustand: 'aus', geraetId: null };
  const reg = await navigator.serviceWorker.getRegistration();
  const abo = await reg?.pushManager.getSubscription();
  if (!abo || abo.endpoint !== lokal.endpoint || Notification.permission !== 'granted') return { zustand: 'verloren', geraetId: lokal.geraetId };
  return { zustand: 'an', geraetId: lokal.geraetId };
}

/** Geraetename fuer die Liste, z. B. "Android · Chrome". */
export function geraeteName(): string {
  const ua = navigator.userAgent;
  const system = /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : 'Gerät';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const app = matchMedia?.('(display-mode: standalone)').matches ? ' (App)' : '';
  return `${system} · ${browser}${app}`;
}

/** Erlaubnis holen, Abo anlegen, beim Dienst anmelden. Auch fuer "neu anmelden". */
export async function pushEinschalten(): Promise<void> {
  const status = await dienstStatus();
  if (!status.publicKey) throw new Error('Der Push-Dienst ist nicht eingerichtet.');
  if ((await Notification.requestPermission()) !== 'granted') throw new Error('Benachrichtigungen wurden nicht erlaubt.');
  const reg = await navigator.serviceWorker.ready;
  const altes = await reg.pushManager.getSubscription();
  if (altes) await altes.unsubscribe();
  const abo = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: schluessel(status.publicKey) });
  const alt = lokalLesen();
  if (alt) await geraetAbmelden(alt.geraetId).catch(() => undefined);
  const { id } = await geraetAnmelden(geraeteName(), abo.toJSON());
  lokalSchreiben({ geraetId: id, endpoint: abo.endpoint });
  await usePush.getState().pruefen();
}

export async function pushAusschalten(): Promise<void> {
  const lokal = lokalLesen();
  const reg = await navigator.serviceWorker.getRegistration();
  await (await reg?.pushManager.getSubscription())?.unsubscribe();
  if (lokal) await geraetAbmelden(lokal.geraetId).catch(() => undefined);
  lokalSchreiben(null);
  await usePush.getState().pruefen();
}

/** Dieses Geraet wurde in der Liste abgemeldet. */
export function lokalVergessen(geraetId: string): void {
  if (lokalLesen()?.geraetId === geraetId) lokalSchreiben(null);
}
